////////LIBRARY/////////
import React, { useRef, useState } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { format } from 'date-fns'
import { fr } from 'date-fns/locale'
///////COMPONENTS///////
import UseOutsideClick from '../../../hooks/UseOutsideClick'

/* Nombre d'annees affichees d'un coup dans le panneau des annees (grille 3x4) */
const YEARS_PER_PAGE = 12

/////////STYLED/////////
const HeaderWrapper = styled.div`
	position: relative;
	display: flex;
	justify-content: center;
	align-items: center;
	height: 40px;
	color: ${(props) => props.theme.colors.white};
	background-color: ${(props) => props.theme.colors.primary};
`
const SelectorButton = styled.button`
	display: flex;
	align-items: center;
	margin: 0 3px;
	padding: 3px 7px;
	border: 0;
	border-radius: 3px;
	background-color: ${(props) => (props.isOpen ? 'rgba(255, 255, 255, 0.25)' : 'transparent')};
	color: ${(props) => props.theme.colors.white};
	font-weight: bold;
	cursor: pointer;
	transition: background-color 0.1s;
	:hover {
		background-color: rgba(255, 255, 255, 0.25);
	}
	:focus {
		outline: none;
	}
`
const Caret = styled.span`
	margin-left: 5px;
	font-size: 9px !important;
	line-height: 1;
`
const Panel = styled.div`
	position: absolute;
	top: 40px;
	left: 50%;
	transform: translateX(-50%);
	z-index: 3;
	width: 216px;
	padding: 6px;
	box-sizing: border-box;
	display: flex;
	flex-wrap: wrap;
	background-color: ${(props) => props.theme.colors.white};
	border: 1px solid ${(props) => props.theme.colors.datePicker.borderInput};
	border-radius: 3px;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
	cursor: default;
`
const PanelNav = styled.div`
	width: 100%;
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 4px;
	color: ${(props) => props.theme.colors.tabText};
	font-weight: bold;
`
const PanelNavButton = styled.button`
	padding: 0 8px;
	border: 0;
	background: none;
	color: ${(props) => (props.disabled ? props.theme.colors.grey : props.theme.colors.primary)};
	font-weight: bold;
	cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
	:focus {
		outline: none;
	}
`
const Cell = styled.button`
	width: calc(33.333% - 4px);
	margin: 2px;
	padding: 6px 0;
	border: 0;
	border-radius: 3px;
	background-color: ${(props) => (props.isSelected ? props.theme.colors.primary : props.theme.colors.datePickerBg)};
	color: ${(props) => (props.disabled ? props.theme.colors.grey : props.isSelected ? props.theme.colors.white : props.theme.colors.black)};
	cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
	transition: background-color 0.1s;
	:hover {
		background-color: ${(props) =>
			props.disabled ? props.theme.colors.datePickerBg : props.isSelected ? props.theme.colors.hoverPrimary : props.theme.colors.whiteGrey};
	}
	:focus {
		outline: none;
	}
`
/////////STYLED/////////

/**
 * En-tete de calendrier permettant de choisir directement le mois et l'annee,
 * en plus des fleches de navigation mois par mois.
 * Destine a la prop `renderCustomHeader` de react-datepicker.
 */
const CalendarHeader = ({
	date,
	changeYear,
	changeMonth,
	decreaseMonth,
	increaseMonth,
	prevMonthButtonDisabled,
	nextMonthButtonDisabled,
	minDate,
	maxDate,
	locale = fr,
}) => {
	const displayedYear = date.getFullYear()
	const displayedMonth = date.getMonth()

	const [openedPanel, setOpenedPanel] = useState(null)
	const [firstYearShown, setFirstYearShown] = useState(displayedYear - 5)
	const wrapperRef = useRef(null)

	UseOutsideClick(wrapperRef, () => setOpenedPanel(null))

	/* Bornes exprimees en nombres : evite toute manipulation de date pour les comparaisons */
	const minYear = minDate ? minDate.getFullYear() : null
	const maxYear = maxDate ? maxDate.getFullYear() : null
	const minMonth = minDate ? minDate.getMonth() : null
	const maxMonth = maxDate ? maxDate.getMonth() : null

	const isMonthDisabled = (monthIndex) => {
		if (minYear !== null && (displayedYear < minYear || (displayedYear === minYear && monthIndex < minMonth))) return true
		if (maxYear !== null && (displayedYear > maxYear || (displayedYear === maxYear && monthIndex > maxMonth))) return true
		return false
	}
	const isYearDisabled = (year) => (minYear !== null && year < minYear) || (maxYear !== null && year > maxYear)

	/* Libelles issus de la meme locale que la grille des jours, pour rester coherent */
	const monthLabels = Array.from({ length: 12 }, (_, index) => format(new Date(2000, index, 1), 'LLL', { locale }))
	const shownYears = Array.from({ length: YEARS_PER_PAGE }, (_, index) => firstYearShown + index)

	const previousYearsDisabled = minYear !== null && firstYearShown <= minYear
	const nextYearsDisabled = maxYear !== null && firstYearShown + YEARS_PER_PAGE - 1 >= maxYear

	///////////////////////////////// HANDLE ///////////////////////////////////////

	const togglePanelHandler = (panel) => {
		/* Le panneau des annees se recentre sur l'annee affichee a chaque ouverture */
		if (panel === 'year' && openedPanel !== 'year') setFirstYearShown(displayedYear - 5)
		setOpenedPanel(openedPanel === panel ? null : panel)
	}
	const selectMonthHandler = (monthIndex) => {
		changeMonth(monthIndex)
		setOpenedPanel(null)
	}
	const selectYearHandler = (year) => {
		changeYear(year)
		setOpenedPanel(null)
	}
	const shiftYearsHandler = (direction) => setFirstYearShown(firstYearShown + direction * YEARS_PER_PAGE)

	///////////////////////////////// RENDER ///////////////////////////////////////

	return (
		<HeaderWrapper ref={wrapperRef}>
			{/* Les fleches disparaissent aux bornes, comme le comportement natif de react-datepicker */}
			{!prevMonthButtonDisabled && (
				<button type="button" className="react-datepicker__navigation react-datepicker__navigation--previous" onClick={decreaseMonth} />
			)}
			<SelectorButton type="button" isOpen={openedPanel === 'month'} onClick={() => togglePanelHandler('month')}>
				{format(date, 'LLLL', { locale })}
				<Caret>▼</Caret>
			</SelectorButton>
			<SelectorButton type="button" isOpen={openedPanel === 'year'} onClick={() => togglePanelHandler('year')}>
				{displayedYear}
				<Caret>▼</Caret>
			</SelectorButton>
			{!nextMonthButtonDisabled && (
				<button type="button" className="react-datepicker__navigation react-datepicker__navigation--next" onClick={increaseMonth} />
			)}

			{openedPanel === 'month' && (
				<Panel>
					{monthLabels.map((monthLabel, monthIndex) => (
						<Cell
							key={monthLabel}
							type="button"
							isSelected={monthIndex === displayedMonth}
							disabled={isMonthDisabled(monthIndex)}
							onClick={() => selectMonthHandler(monthIndex)}
						>
							{monthLabel}
						</Cell>
					))}
				</Panel>
			)}

			{openedPanel === 'year' && (
				<Panel>
					<PanelNav>
						<PanelNavButton type="button" disabled={previousYearsDisabled} onClick={() => shiftYearsHandler(-1)}>
							{'‹'}
						</PanelNavButton>
						<span>{`${shownYears[0]} - ${shownYears[shownYears.length - 1]}`}</span>
						<PanelNavButton type="button" disabled={nextYearsDisabled} onClick={() => shiftYearsHandler(1)}>
							{'›'}
						</PanelNavButton>
					</PanelNav>
					{shownYears.map((year) => (
						<Cell
							key={year}
							type="button"
							isSelected={year === displayedYear}
							disabled={isYearDisabled(year)}
							onClick={() => selectYearHandler(year)}
						>
							{year}
						</Cell>
					))}
				</Panel>
			)}
		</HeaderWrapper>
	)
}

CalendarHeader.propTypes = {
	date: PropTypes.object.isRequired,
	changeYear: PropTypes.func.isRequired,
	changeMonth: PropTypes.func.isRequired,
	decreaseMonth: PropTypes.func.isRequired,
	increaseMonth: PropTypes.func.isRequired,
	prevMonthButtonDisabled: PropTypes.bool,
	nextMonthButtonDisabled: PropTypes.bool,
	minDate: PropTypes.object,
	maxDate: PropTypes.object,
	locale: PropTypes.object,
}

export default CalendarHeader
